import { useMemo } from 'react'
import { useArchiveViewStore } from '../store/archiveViewStore.js'
import { useMetaData } from '../queries/useMetaData.js'
import { weekLabel } from '../data/metaKeys.js'
import { useQueryClient } from '@tanstack/react-query'
import { DEFAULT_META } from '../data/metaApi.js'

/**
 * ArchiveViewBanner — sticky yellow bar shown at the top of the
 * dashboard when we're viewing a past week's archive.
 *
 * Hidden entirely when archive view is inactive; takes over as the
 * topmost interactive element when active. Provides:
 *
 *   - Week label (e.g. "Viewing week of April 14, 2026")
 *   - "Frozen · read-only" disclosure
 *   - Prev / Next week buttons (disabled when no adjacent snapshot)
 *   - "Back to current" exit button
 *
 * Read-only enforcement happens elsewhere (edit components call
 * useIsReadOnly() and short-circuit). The banner just signals and
 * provides navigation.
 */
export default function ArchiveViewBanner() {
  const active = useArchiveViewStore((s) => s.active)
  const weekKey = useArchiveViewStore((s) => s.weekKey)
  const setWeekKey = useArchiveViewStore((s) => s.setWeekKey)
  const exitArchive = useArchiveViewStore((s) => s.exitArchive)

  // useMetaData returns the ARCHIVED meta when active, so we reach
  // into the React Query cache to get the live weeklyHistory for
  // prev/next navigation. The live cache is always available — the
  // archive override only swaps the reader, not the underlying cache.
  const queryClient = useQueryClient()
  // Subscribe via useMetaData too so the banner re-renders when
  // someone in another tab creates a new snapshot.
  useMetaData()

  const weekKeys = useMemo(() => {
    const liveMeta = queryClient.getQueryData(['meta']) || DEFAULT_META
    const history = liveMeta?.weeklyHistory || {}
    return Object.keys(history).sort()
  }, [queryClient, weekKey])

  if (!active || !weekKey) return null

  const idx = weekKeys.indexOf(weekKey)
  const prevKey = idx > 0 ? weekKeys[idx - 1] : null
  const nextKey = idx >= 0 && idx < weekKeys.length - 1 ? weekKeys[idx + 1] : null

  return (
    <div
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 900,
        background: '#fef3c7',
        borderBottom: '1px solid #fde68a',
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        fontSize: 12,
        color: '#78350f',
        flexWrap: 'wrap',
      }}
    >
      <span style={{ fontSize: 14 }}>🕰️</span>
      <div style={{ flex: 1, minWidth: 200 }}>
        <span style={{ fontWeight: 600 }}>Viewing week of {weekLabel(weekKey)}</span>
        <span style={{ color: '#92400e' }}> · Frozen · read-only</span>
      </div>
      <button
        type="button"
        onClick={() => prevKey && setWeekKey(prevKey)}
        disabled={!prevKey}
        title={prevKey ? `Jump to week of ${weekLabel(prevKey)}` : 'No earlier snapshot'}
        style={navBtn(!prevKey)}
      >
        ‹ Prev week
      </button>
      <button
        type="button"
        onClick={() => nextKey && setWeekKey(nextKey)}
        disabled={!nextKey}
        title={nextKey ? `Jump to week of ${weekLabel(nextKey)}` : 'No later snapshot'}
        style={navBtn(!nextKey)}
      >
        Next week ›
      </button>
      <button
        type="button"
        onClick={exitArchive}
        style={{
          padding: '4px 12px',
          border: 'none',
          background: '#78350f',
          color: '#fff',
          fontSize: 11,
          fontWeight: 700,
          borderRadius: 6,
          cursor: 'pointer',
        }}
      >
        Back to current
      </button>
    </div>
  )
}

function navBtn(disabled) {
  return {
    padding: '4px 10px',
    border: '1px solid #fde68a',
    background: disabled ? '#fef3c7' : '#fff',
    color: '#78350f',
    fontSize: 11,
    fontWeight: 600,
    borderRadius: 6,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
  }
}
